import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Pagination,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { selectAllCompanies } from '../../slices/companies/companiesSlice';
import { fetchSuppliers } from '../../slices/suppliers/suppliersSlice';
import { EmployeesCreateModal } from '../company-create/components/modal/EmployeesCreateModal';
import Button from '@mui/material/Button';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../common/constants/routes';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import { Supplier } from '../../models/supplier.model';
import styles from './SupplierProfiles.module.scss';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { NWClient } from '../../client/NWClient';
import MessageModal from '../../common/message-modal/MessageModal';
import { useSupplierProfiles } from './hooks/useSupplierProfiles';
import DashboardIcon from '../../../assets/svg/dashboard.svg?react';
import BallotIcon from '../../../assets/svg/ballot.svg?react';
import { SuppliersTable } from './components/SuppliersTable';
import { SupplierRow } from './components/SupplierRow';
import { ProgressOverlay } from '../../common/progress-overlay/ProgressOverlay';
import InputAdornment from '@mui/material/InputAdornment';
import CloseIcon from '@mui/icons-material/Close';
import { useCheckPermission } from '../../common/hooks/useCheckPermission';
import { USER_PERMISSIONS } from '../../common/constants/permissions';
import InfoIcon from '@mui/icons-material/Info';
import PreviewTooltip from '../../common/preview-block/PreviewTooltip';
import Preview from './components/preview-block/Preview';
import { Helpers } from '../../common/helpers/helpers';

const SupplierProfiles = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isOpenSuppliersCreation, setIsOpenSuppliersCreation] = useState(false);
  const [planMessageVisible, setPlanMessageVisible] = useState(false);
  const [deleteMessageModalOpen, setDeleteMessageModalOpen] = useState(false);

  const {
    suppliers: suppliersCreatedByAdmin,
    setCurrentPage,
    suppliersCount,
    currentPage,
    getRequestedPage,
    cardView,
    setCardView,
    suppliersLoading,
    resetSearchFilter,
    searchCleared,
    setSearchCleared,
    setSearchStringInputValue,
    searchStringInputValue,
  } = useSupplierProfiles();

  // Items per page - adjust this value according to your API
  const itemsPerPage = 20;
  // Calculate total pages
  const totalPages = Math.ceil(suppliersCount / itemsPerPage);

  // Handle page change
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    getRequestedPage(page);
  };

  const handleClose = () => {
    setIsOpenSuppliersCreation(false);
  };
  const company = useAppSelector(selectAllCompanies)[0];
  const checkPermission = useCheckPermission();

  const [supplierId, setSupplierId] = useState<number | null>(null);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>, id: number) => {
    setAnchorEl(event.currentTarget);
    setSupplierId(id);
  };
  const handleMoreVertClose = () => {
    setAnchorEl(null);
  };

  const handleProfileDelete = () => {
    if (supplierId) {
      setDeleteMessageModalOpen(false);
      NWClient.delete('supplier_profile', supplierId).then(() => {
        setSupplierId(null);
        dispatch(fetchSuppliers({ company_id: company?.id }));
        setAnchorEl(null);
      });
    }
  };

  const handleEdit = (id: number) => {
    navigate(ROUTES.SUPPLIER_PROFILES + '/' + id + '?initialTab=1');
  };

  const handleDocuments = (id: number) => {
    navigate(ROUTES.SUPPLIER_PROFILES + '/' + id + '?initialTab=0');
  };

  const handleDelete = (id: number) => {
    setSupplierId(id);
    setDeleteMessageModalOpen(true);
  };

  const handleCreateSupplier = () => {
    const planSettings = Helpers.getPlanSettings(company);
    if (!planSettings) return;

    const limit = planSettings.supplier_profile;
    const count = suppliersCreatedByAdmin?.length ?? 0;

    if (limit === 0) {
      setPlanMessageVisible(true);
    } else if (limit !== 'unlimited' && count >= limit) {
      setPlanMessageVisible(true);
    } else {
      navigate(ROUTES.SUPPLIER_PROFILE_CREATE);
    }
  };

  return (
    <Grid container flexWrap='wrap' sx={{ mt: '-61px' }}>
      <Typography variant='h1' component='h1' className={styles.page_title}>
        <span style={{ marginRight: '8px' }}>{t('supplierProfiles.header')}</span>
        <PreviewTooltip title={<Preview />} arrow placement='top'>
          <InfoIcon color='primary' fontSize='small' />
        </PreviewTooltip>
      </Typography>

      <Box display='flex' mt='8px' width='100%' justifyContent='space-between' alignItems='center'>
        {suppliersCount && suppliersCount > 20 ? (
          <Button
            onClick={handleCreateSupplier}
            className='btn-default btn-action-basic'
            variant='contained'
            color='primary'
          >
            <Typography fontWeight='light' fontSize={14}>
              {t('buttons.addProfile')}
            </Typography>
          </Button>
        ) : null}
        <Box display={'flex'} justifyContent={'flex-start'} alignItems={'center'} gap={'8px'}>
          <TextField
            label={t('labels.searchByName')}
            className='search-input'
            InputLabelProps={{ style: { backgroundColor: '#f4f9fd' } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    disabled={!searchStringInputValue.trim()}
                    onClick={() => resetSearchFilter()}
                  >
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            value={searchStringInputValue}
            onChange={(e) => {
              setSearchStringInputValue(e.target.value);
              if (e.target.value.trim().length > 0) {
                setSearchCleared(false);
              }
            }}
            onKeyUp={(e) => {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              if (e.target.value.trim() === '' && !searchCleared) {
                resetSearchFilter();
              }
            }}
          />
          {suppliersCount && suppliersCount > 20 ? (
            <Box className='pagination-block' sx={{ display: 'flex', justifyContent: 'end' }}>
              <Typography className='pagination-pages-value' variant='caption'>
                Page {currentPage} of {totalPages}
              </Typography>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                siblingCount={0}
                boundaryCount={0}
                sx={{
                  '& .MuiPagination-ul': { gap: 0 },
                }}
              />
            </Box>
          ) : null}
          <Box className={styles.card_list_view_container}>
            <button
              className={`${styles.variant_btn} + ${cardView ? styles.variant_btn_active : ''}`}
              onClick={() => setCardView(true)}
            >
              <DashboardIcon />
              <span>{t('buttons.card')}</span>
            </button>
            <button
              onClick={() => {
                setCardView(false);
              }}
              className={`${styles.variant_btn} + ${!cardView ? styles.variant_btn_active : ''}`}
            >
              <BallotIcon />
              <span>{t('buttons.list')}</span>
            </button>
          </Box>
        </Box>
      </Box>
      <Grid size={{ xs: 12 }} className={styles.supplier_cards}>
        <>
          {suppliersLoading === 'loading' && <ProgressOverlay className='position-absolute' />}
          {cardView ? (
            <>
              {[...suppliersCreatedByAdmin]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((supplier: Supplier) => (
                  <Card
                    tabIndex={-1}
                    key={supplier.id}
                    onClick={() => navigate(ROUTES.SUPPLIER_PROFILES + '/' + supplier.id)}
                    className={styles.supplier_card}
                  >
                    {supplier.name && (
                      <Typography
                        fontWeight='regular'
                        className={`${styles.supplier_name} ${
                          supplier.name && supplier.name.length > 30 ? styles.long_text : ''
                        }`}
                        title={supplier.name}
                      >
                        {supplier.name}
                      </Typography>
                    )}
                    <Box className={styles.supplier_profile_icons}>
                      {checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_UPLOAD_CREATE) ||
                      checkPermission(USER_PERMISSIONS.ORG_SUPPLIER_PROFILE_UPDATE_DOCUMENT) ||
                      checkPermission(USER_PERMISSIONS.ORG_SUPPLIER_PROFILE_DELETE_DOCUMENT) ? (
                        <IconButton
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDocuments(supplier.id);
                          }}
                          size='small'
                        >
                          <UploadFileOutlinedIcon fontSize='small' />
                        </IconButton>
                      ) : (
                        <Tooltip title={t('messages.contactAdministrator')} arrow placement='top'>
                          <IconButton disabled={true} onClick={null} size='small'>
                            <UploadFileOutlinedIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      )}
                      {checkPermission(USER_PERMISSIONS.ORG_SUPPLIER_PROFILE_UPDATE) ? (
                        <IconButton
                          onClick={(event) => {
                            event.stopPropagation();
                            handleEdit(supplier.id);
                          }}
                          size='small'
                          sx={{ p: 0.5 }}
                        >
                          <EditIcon fontSize='small' />
                        </IconButton>
                      ) : (
                        <Tooltip title={t('messages.contactAdministrator')}>
                          <IconButton onClick={null} disabled={true} size='small' sx={{ p: 0.5 }}>
                            <EditIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Box sx={{ flexGrow: 1 }} />
                      <IconButton
                        aria-label={t('labels.menuButton')}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleClick(event, supplier.id);
                        }}
                        size='small'
                        sx={{ p: 0.5 }}
                      >
                        <MoreVertIcon fontSize='small' />
                      </IconButton>
                    </Box>
                  </Card>
                ))}
              <Button
                onClick={handleCreateSupplier}
                variant='contained'
                color='primary'
                disabled={!checkPermission(USER_PERMISSIONS.ORG_SUPPLIER_PROFILE_CREATE)}
                className={styles.add_button}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'column',
                    gap: '1rem',
                  }}
                >
                  <Typography fontWeight='light' fontSize={14}>
                    {t('buttons.addProfile')}
                  </Typography>
                  <AddIcon />
                </Box>
              </Button>
            </>
          ) : (
            <SuppliersTable>
              {[...suppliersCreatedByAdmin].map((supplier) => (
                <SupplierRow
                  supplier={supplier}
                  key={supplier.id}
                  handleDelete={() => handleDelete(supplier.id)}
                  handleEdit={() => handleEdit(supplier.id)}
                  navigateToDocs={() => handleDocuments(supplier.id)}
                />
              ))}
            </SuppliersTable>
          )}
        </>

        {suppliersCount && suppliersCount > 20 ? (
          <Box
            display='flex'
            alignItems='center'
            justifyContent='flex-end'
            marginTop='8px'
            width='100%'
          >
            <Box className='pagination-block' sx={{ display: 'flex', justifyContent: 'end' }}>
              <Typography className='pagination-pages-value' variant='caption'>
                Page {currentPage} of {totalPages}
              </Typography>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                siblingCount={0}
                boundaryCount={0}
                sx={{
                  '& .MuiPagination-ul': { gap: 0 },
                }}
              />
            </Box>
          </Box>
        ) : null}
      </Grid>
      <EmployeesCreateModal open={isOpenSuppliersCreation} onClose={handleClose} />

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMoreVertClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem
          disabled={!checkPermission(USER_PERMISSIONS.ORG_SUPPLIER_PROFILE_DELETE)}
          onClick={() => setDeleteMessageModalOpen(true)}
        >
          {t('buttons.delete')}
        </MenuItem>
      </Menu>
      {planMessageVisible && (
        <MessageModal
          open={planMessageVisible}
          modalMessage={t('messages.limitSuppliers')}
          actionButtonText={t('buttons.ok')}
          actionButtonFn={() => navigate(ROUTES.PRICING)}
          goBackButtonText={t('buttons.later')}
          onClose={() => {
            setPlanMessageVisible(false);
          }}
        />
      )}
      {deleteMessageModalOpen && (
        <MessageModal
          open={deleteMessageModalOpen}
          modalTitle={t('messages.deleteSupplier')}
          modalMessage={t('messages.deleteSupplierDialog')}
          actionButtonText={t('buttons.goBack')}
          actionButtonFn={() => setDeleteMessageModalOpen(false)}
          goBackButtonText={t('buttons.delete')}
          onClose={() => {
            handleProfileDelete();
          }}
        />
      )}
    </Grid>
  );
};

export default SupplierProfiles;
